import React, { useMemo } from "react";
import theme from "../../data/theme";
import { tierCliff } from "../../utils/liveBoard";

/**
 * TierCliff — per position: the best player left, the next one, the gap between
 * them, and how many are left in that tier.
 *
 * The tier count is the part a plain value list cannot do, and it answers the
 * complaint the r/ff thread agreed on most: being the last person to take
 * someone in the last tier.
 *
 * 🔴 OBSERVATIONAL ONLY. Every string here states what is on the board. There
 * is no suggested bid, no "buy now", no urgency language beyond the arithmetic
 * itself — a large gap is reported as a percentage, and the reader decides what
 * it means.
 */
const DROP_FLAG = 25;    // percent. Flagged, not acted on.

/**
 * 🔴 A2 — WHEN THE BOARD'S FORMAT DIVERGES FROM APP SETTINGS.
 * dynamicPrices is built from the app's league settings; the board's budget and
 * team count come from board setup. They normally agree because setup prefills
 * from settings, but they can differ.
 *
 * When they do, the DOLLARS are simply wrong — every price in the map is
 * denominated in the other league's budget. The RATIO between best and
 * next-best survives a rescale largely intact, and the tier count does not
 * depend on price at all, so those two still say something true.
 *
 * So: keep the drop percentage and the "N left in tier" count, suppress the
 * dollar figures, and say why in one muted line. Showing nothing beats showing
 * wrong on a screen eleven people are reading.
 *
 * Team count is the messier half — it moves tier boundaries by rank rather than
 * scaling prices — which is why the line names both and claims only that the
 * shape is still readable.
 */
export default function TierCliff({ board, dynamicPrices, priceSettings }) {
  const rows = useMemo(() => tierCliff(board, dynamicPrices), [board, dynamicPrices]);

  const budgetDiffers = !!priceSettings && priceSettings.budget != null
    && priceSettings.budget !== board.budget;
  const teamsDiffer = !!priceSettings && priceSettings.teams != null
    && priceSettings.teams !== board.teamNames.length;
  const showDollars = !budgetDiffers && !teamsDiffer;

  const usable = rows.filter(r => r.best);
  if (!usable.length) return null;

  return (
    <div style={{
      background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`,
      borderRadius: theme.radius.lg, padding: "10px 14px",
    }}>
      <div style={{
        fontSize: theme.fontSize.micro, textTransform: "uppercase", letterSpacing: "0.08em",
        color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold, marginBottom: 8,
      }}>Best left, and the drop to the next</div>

      {!showDollars && (
        <div style={{ fontSize: theme.fontSize.micro, color: theme.colors.text.faint, marginBottom: 8, lineHeight: 1.5 }}>
          Dollar estimates are hidden: this board is set to ${board.budget} and {board.teamNames.length} teams,
          but prices are built from your saved league
          {priceSettings?.budget != null && ` ($${priceSettings.budget}`}
          {priceSettings?.teams != null && `, ${priceSettings.teams} teams`}
          {priceSettings?.budget != null && `)`}. The drop between players and the tier counts still hold.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
        {usable.map(r => {
          const steep = r.dropPct != null && r.dropPct >= DROP_FLAG;
          return (
            <div key={r.pos} style={{
              display: "flex", flexDirection: "column", gap: 3,
              borderLeft: `3px solid ${theme.colors.pos[r.pos]}`, paddingLeft: 9,
            }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <strong style={{ color: theme.colors.pos[r.pos], fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.heavy }}>{r.pos}</strong>
                <span style={{
                  fontSize: theme.fontSize.body, color: theme.colors.text.primary,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{r.best.name}</span>
                {showDollars && (
                  <span style={{
                    marginLeft: "auto", fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.bold,
                    color: theme.colors.brand.accent, fontVariantNumeric: "tabular-nums",
                  }}>${r.best.price}</span>
                )}
              </div>
              {r.next && (
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: theme.fontSize.caption, color: theme.colors.text.muted }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>then {r.next.name}</span>
                  {showDollars && (
                    <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>${r.next.price}</span>
                  )}
                  <span style={{
                    marginLeft: showDollars ? undefined : "auto",
                    fontVariantNumeric: "tabular-nums", fontWeight: theme.fontWeight.bold,
                    color: steep ? "#fbbf24" : theme.colors.text.faint,
                  }}>−{Math.round(r.dropPct)}%</span>
                </div>
              )}
              <div style={{ fontSize: theme.fontSize.micro, color: theme.colors.text.faint }}>
                {r.tierLeft} left in tier {r.tier}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
